import PropTypes from 'prop-types';
import React from 'react';
import { useLocation, useHistory } from 'react-router-dom';
import cx from 'classnames';
import {
  Button,
  Icon,
  Resource,
  ResourceBody,
  ResourceIntro,
  ResourceRight,
  ResourceGroup,
  UtilitySystem,
} from 'rhinostyle';
import PageLoader from './PageLoader';
import MemberProfilePanelContainer from '../containers/MemberProfilePanelContainer';
import { UserHelpers } from '../helpers';
import { AppConstants } from '../constants';
import { MEMBER_CREATE } from '../constants/UserPermissionsConstants';
import EmptyMessage from './EmptyMessage';
import Tags from './Tags';
import LazyLoadList from './LazyLoadList';

const OrganizationMembers = (props) => {
  const history = useHistory();
  const location = useLocation();
  const {
    activeMember,
    activeMemberId,
    activeMemberIds,
    handleCreateRequest,
    handleToggleActive,
    handleTogglePanel,
    inactiveMemberIds,
    memberFetchLoading,
    panelOpen,
    users,
    usersLoading,
    totalUserCount,
    viewActive,
    isSSOEnabled,
    isCcr,
    handleLoadMore,
  } = props;

  const { userHasAnyOfPermissions } = UserHelpers;

  const renderMember = (id, key) => {
    const member = users[id];
    const { onlineStatus } = member; // This property is being set explicitly in the WebSocketService and is not part of the User response object
    const active = activeMemberId === id;
    const profileImageUrl = member.profileImageUrl ? `${AppConstants.AVATAR_BASE_URL}${member.profileImageUrl}` : '';
    const avatar = {
      name: UserHelpers.formatAvatarName(member.firstName, member.lastName),
      type: 'member',
      image: profileImageUrl,
      showOnlineStatus: true,
      onlineStatus,
    };

    return (
      <Resource active={active} key={key} onClick={() => handleTogglePanel(id)}>
        <ResourceIntro title={UserHelpers.formatMemberNameWithPrefixAndSuffix(member)} avatar={avatar}>
          {member.isInviteSent === false && <span className="u-text-accent">Invite not sent</span>}
        </ResourceIntro>
        {member.tags?.length > 0 && <ResourceBody className="u-text-muted"><Tags tagIds={member.tags} /></ResourceBody>}
        <ResourceRight>
          {member.groups && member.groups.length} group{member.groups.length !== 1 && 's'}
          {!member.active && (
            <>
              <br /><span className="u-text-accent">Deactivated</span>
            </>
          )}
        </ResourceRight>
      </Resource>
    );
  };

  const renderNoResults = () => <div className="member-list__nocontacts_wrapper"><EmptyMessage section="Members" /></div>;

  const renderActive = () => (
    <ResourceGroup>
      {activeMemberIds.map(renderMember)}
    </ResourceGroup>
  );

  const renderDeactivated = () => (
    <ResourceGroup>
      {inactiveMemberIds.map(renderMember)}
    </ResourceGroup>
  );

  const appPanelClasses = cx('app-panels', {
    'app-panels--profile': panelOpen,
  });

  function getCanFetchMoreMembers() {
    const membersLoaded = viewActive ? activeMemberIds : inactiveMemberIds;
    return totalUserCount > membersLoaded?.length;
  }

  const tabTriggerActiveClasses = `tabs__trigger u-m-r ${viewActive ? UtilitySystem.config.classes.active : ''}`;
  const tabTriggerDeactedClasses = `tabs__trigger ${!viewActive ? UtilitySystem.config.classes.active : ''}`;

  return (
    <div className="app-page">
      <div className={appPanelClasses}>
        <div className="list-panel__wrapper">
          <div className="list-panel">
            <div className="app-page__header">
              <div className="app-page__header__title">Members</div>
              {(!isSSOEnabled || isCcr) && userHasAnyOfPermissions([MEMBER_CREATE]) && (
                <div className="app-page__header__action">
                  <Button
                    onClick={handleCreateRequest}
                    iconOnly
                    type="secondary"
                    title="Create Member"
                    data-cypress="createMemberProfile"
                  >
                    <Icon icon="add" />
                  </Button>
                </div>
              )}
            </div>
            <div className="u-flex u-flex-justify-center">
              <div className="sub-nav__tabs__header">
                <Button reset className={tabTriggerActiveClasses} onClick={() => handleToggleActive()} data-cypress="active">
                  Active
                </Button>
                <Button reset className={tabTriggerDeactedClasses} onClick={() => handleToggleActive()} data-cypress="deactivate">
                  Deactivated
                </Button>
              </div>
            </div>
            {usersLoading ? (
              <PageLoader />
            ) : (
              <>
                {totalUserCount > 0 ? (
                  <div className="list-panel__body">
                    <>
                      <div className="member-section__header">
                        {viewActive ? (
                          <span className="member-section__label"> Total Active Members ({ totalUserCount }) </span>
                        ) : (
                          <span className="member-section__label"> Total Deactivated Members ({ totalUserCount })</span>
                        )}
                      </div>
                      <LazyLoadList
                        handleLoadMore={handleLoadMore}
                        loading={memberFetchLoading}
                        className="list-panel__body__scroll"
                        disabled={!getCanFetchMoreMembers()}
                      >
                        {viewActive ? renderActive() : renderDeactivated() }
                      </LazyLoadList>
                    </>
                  </div>
                ) : (
                  <div>
                    {renderNoResults()}
                  </div>
                )}
              </>
            )}
          </div>
        </div>
        {activeMember && panelOpen && (
          <MemberProfilePanelContainer
            activeUser={activeMember}
            handleTogglePanel={handleTogglePanel}
            history={history}
            location={location}
          />
        )}
      </div>
    </div>
  );
};

OrganizationMembers.propTypes = {
  activeMember: PropTypes.object,
  activeMemberId: PropTypes.number,
  activeMemberIds: PropTypes.array.isRequired,
  handleCreateRequest: PropTypes.func.isRequired,
  handleToggleActive: PropTypes.func.isRequired,
  handleTogglePanel: PropTypes.func.isRequired,
  inactiveMemberIds: PropTypes.array.isRequired,
  memberFetchLoading: PropTypes.bool.isRequired,
  panelOpen: PropTypes.bool.isRequired,
  users: PropTypes.object.isRequired,
  usersLoading: PropTypes.bool.isRequired,
  totalUserCount: PropTypes.number.isRequired,
  viewActive: PropTypes.bool.isRequired,
  isSSOEnabled: PropTypes.bool.isRequired,
  isCcr: PropTypes.bool.isRequired,
  handleLoadMore: PropTypes.func,
};

export default OrganizationMembers;
